import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  HiOutlineFolder,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
} from "react-icons/hi2";

import { Link } from "react-router-dom";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";
import PageEmpty from "../../../components/ui/pageEmpty/PageEmpty";
import PageCard from "../../../components/ui/pageCard/PageCard";
import Modal from "../../../components/ui/modal/Modal";

import DossierForm from "../../../components/forms/dossier/DossierForm";

import usePermissions from "../../../hooks/usePermissions";

import {
  getDossiers,
  setDossierFilters,
} from "../../../redux/features/dossiers/dossierSlice";

import "./DossiersPage.css";

export default function DossiersPage() {
  const dispatch = useDispatch();

  const { can } = usePermissions();

  const { dossiers, filters, isLoading, error } = useSelector(
    (state) => state.dossiers,
  );

  const [searchValue, setSearchValue] = useState(filters.search || "");

  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [openCreateModal, setOpenCreateModal] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      dispatch(
        setDossierFilters({
          search: searchValue,
        }),
      );
    }, 400);

    return () => clearTimeout(timer);
  }, [dispatch, searchValue]);

  useEffect(() => {
    dispatch(getDossiers(filters)).finally(() => {
      setHasLoadedOnce(true);
    });
  }, [dispatch, filters]);

  const handleSearchChange = (event) => {
    setSearchValue(event.target.value);
  };

  if (isLoading && !hasLoadedOnce) {
    return <PageLoader title="Dossiers laden..." />;
  }

  return (
    <div className="dossiers-page">
      <PageHeader
        eyebrow="POWER"
        title="Dossiers"
        description="Overzicht van alle dossiers."
        actions={
          can("dossier.create") && (
            <button
              className="dossiers-page__create-button"
              onClick={() => setOpenCreateModal(true)}
            >
              <HiOutlinePlus />

              <span>Nieuw dossier</span>
            </button>
          )
        }
      />

      {error && <div className="dossiers-page__error">{error}</div>}

      <PageCard className="dossiers-page__filters">
        <div className="dossiers-page__search">
          <HiOutlineMagnifyingGlass />

          <input
            name="search"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="Zoeken..."
          />
        </div>
      </PageCard>

      {isLoading && hasLoadedOnce && (
        <div className="dossiers-page__small-loading">
          Dossiers bijwerken...
        </div>
      )}

      {dossiers.length === 0 ? (
        <PageEmpty
          icon={<HiOutlineFolder />}
          title="Geen dossiers gevonden"
          description="Er zijn momenteel geen dossiers beschikbaar."
        />
      ) : (
        <div className="dossiers-page__grid">
          {dossiers.map((dossier) => (
            <Link
              key={dossier._id}
              to={`/dashboard/dossiers/${dossier._id}`}
              className="dossiers-page__card-link"
            >
              <PageCard className="dossiers-page__card dossiers-page__card--clickable">
                <div className="dossiers-page__card-top">
                  <div>
                    <h3>
                      {dossier.client?.display_name ||
                        dossier.client?.first_name ||
                        "Onbekende cliënt"}
                    </h3>

                    <span>
                      #
                      {dossier.sequence_number ||
                        dossier.dossier_number ||
                        dossier._id}
                    </span>
                  </div>

                  <div
                    className={`dossiers-page__status dossiers-page__status--${dossier.status}`}
                  >
                    {dossier.status || "onbekend"}
                  </div>
                </div>

                <div className="dossiers-page__meta">
                  <div>
                    <strong>Coach</strong>

                    <span>
                      {dossier.coach
                        ? `${dossier.coach.first_name} ${dossier.coach.last_name}`
                        : "-"}
                    </span>
                  </div>

                  <div>
                    <strong>Stap</strong>

                    <span>{dossier.current_step || "-"}</span>
                  </div>

                  <div>
                    <strong>Schooljaar</strong>

                    <span>{dossier.school_year?.name || "-"}</span>
                  </div>
                </div>
              </PageCard>
            </Link>
          ))}
        </div>
      )}

      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Nieuw dossier"
        description="Maak een nieuw dossier aan en wijs het toe aan een coach."
      >
        <DossierForm
          onSuccess={() => {
            setOpenCreateModal(false);

            dispatch(getDossiers(filters));
          }}
          onCancel={() => setOpenCreateModal(false)}
        />
      </Modal>
    </div>
  );
}
